import { getAddressForm } from '@repo/lib/address';
import { z } from 'zod';
import { getCountryCode } from '../../../utils/searchParams';

export type MethodData = {
  returnMethod?: {
    type?: string;
    location?: string;
    carrier?: string;
    address?: Record<string, string>;
    isAddressRequired?: boolean;
    isLocationRequired?: boolean;
  };
  locations?: {
    postalCode?: string;
  };
};

export const MethodTaskSchema = z
  .object({
    type: z.preprocess(
      (value) => (value === null || value === undefined ? '' : value),
      z.string().min(1, { message: 'Return method is required' }),
    ),
    // Optional at the key level: methods without a drop-off location (e.g. in-store,
    // ship-on-your-own) omit it entirely. Under Zod 4 a union containing z.undefined()
    // no longer makes a missing object key valid — the key must be .optional().
    location: z
      .union([z.string(), z.literal(''), z.null()])
      .refine(
        (value) =>
          value === null ||
          value === undefined ||
          value === '' ||
          value.length > 0,
        {
          message: 'Return location is required for this method',
        },
      )
      .optional(),
    carrier: z.string().optional(),
    address: z.record(z.string(), z.string().optional()).optional(),
    isAddressRequired: z.boolean().optional(),
    // Set by the task, like `isAddressRequired`, when the authored page asks the shopper to
    // choose a location themselves rather than starting on the nearest one.
    isLocationRequired: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.isLocationRequired === true && !data.location) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Return location is required for this method',
        path: ['location'],
      });
    }

    if (data.isAddressRequired === true) {
      const countryCode = getCountryCode();
      const validation = getAddressForm(countryCode).validation;

      for (const field in validation) {
        const fieldValidations = validation[field];
        if (!fieldValidations) break;
        const { required, pattern } = fieldValidations;
        const value = data.address?.[field]?.trim();

        if (required && !value) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${field} is required`,
            path: ['address', field],
          });
        }

        if (pattern && !new RegExp(pattern).test(value || '')) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${field} is invalid`,
            path: ['address', field],
          });
        }
      }
    }
  });
